import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, Alert,
  CircularProgress, Switch, FormControlLabel, TextField, Tabs, Tab, Select, MenuItem, LinearProgress, Collapse,
  IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { adminClient } from '../data/adminClient';
import { isConfirmedStatus } from '../../shared/workshopOptions';
import { useEventReadOnly } from '../adminAccess';

const sessionLabel = (s) => `${s.name}${s.time ? ` · ${s.time}` : ''}`;
const dancerName = (d) => `${d.firstName} ${d.lastName}`.trim();

// Admin › Workshops › Dancer Assignments. Release the schedule to teachers,
// watch progress, see who's in each workshop, move dancers, and print
// rosters. Teachers choose on their portal's Workshops page (school spots
// apply to them); changes made here skip the school spots but not the seats.
export default function AssignmentsSection() {
  // View-only admin: release/spots shown but locked, no Move dancers tab.
  const readOnly = useEventReadOnly();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState('workshops');
  const [buffer, setBuffer] = useState('0');
  const [savingRelease, setSavingRelease] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(null); // true | false while confirming

  const showView = (d) => { setData(d); setBuffer(String(d.release.buffer)); };
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await adminClient.getWorkshopAssignments();
      setData(d);
      setBuffer(String(d.release.buffer));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRelease = async (changes) => {
    setSavingRelease(true); setError(null);
    try {
      // The save sends back the updated page — no second request.
      const { release, view } = await adminClient.saveWorkshopRelease(changes);
      setNotice({ severity: 'success', text: changes.released === undefined ? 'Saved.' : release.released ? 'The schedule is now released to teachers.' : 'The schedule is hidden from teachers again.' });
      showView(view);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingRelease(false);
      setConfirmRelease(null);
    }
  };

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!data) return error ? <Alert severity="error">{error}</Alert> : null;

  const total = data.dancers.length;
  const complete = data.dancers.filter(d => d.session1 && d.session2).length;
  const stale = data.dancers.filter(d => d.stale).length;
  const unconfirmed = data.runs.filter(r => !isConfirmedStatus(r.leaderStatus));
  const seats = { 1: 0, 2: 0 };
  data.runs.forEach(r => { seats[r.session] += r.capacity; });

  return (
    <>
      {/* Release */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Switch checked={data.release.released} disabled={readOnly || savingRelease} onChange={e => setConfirmRelease(e.target.checked)} />}
            label={<Typography fontWeight={600}>{data.release.released ? 'Released to teachers' : 'Not released to teachers'}</Typography>}
          />
          {data.release.released && data.release.releasedAt && (
            <Typography variant="body2" color="text.secondary">since {new Date(data.release.releasedAt).toLocaleDateString()}</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <TextField size="small" type="number" label="Extra spots per school" value={buffer} sx={{ width: 180 }}
            inputProps={{ min: 0, max: 20 }} onChange={e => setBuffer(e.target.value)} disabled={readOnly}
            helperText="Added to each school’s share of every workshop" />
          {!readOnly && <Button size="small" variant="outlined" disabled={savingRelease || String(data.release.buffer) === String(Number(buffer) || 0)}
            onClick={() => saveRelease({ buffer: Number(buffer) || 0 })}>Save</Button>}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Each school gets spots in every workshop in proportion to its number of dancers, so dancers from each school spread out.
          {' '}
          {data.hasTimelineRow
            ? (data.locked
              ? <>The <strong>{data.taskTitle}</strong> deadline ({data.deadline}) has passed — teachers can no longer change choices; you can still move dancers below.</>
              : <>Teachers can change choices until <strong>{data.deadline || 'the date on the Timeline'}</strong> (the “{data.taskTitle}” Timeline row).</>)
            : null}
        </Typography>
        {!data.hasTimelineRow && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Add a row named <strong>{data.taskTitle}</strong> to the Timeline to set the teachers’ deadline. Until then, teachers can change choices at any time.
          </Alert>
        )}
      </Paper>

      {/* Summary + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip size="small" color={total && complete === total ? 'success' : 'default'} variant="outlined" label={`${complete} / ${total} dancers have both workshops`} />
        {[1, 2].map(s => (
          <Chip key={s} size="small" variant="outlined" color={seats[s] < total ? 'warning' : 'default'}
            label={`${data.sessions[s].name}: ${data.dancers.filter(d => d[`session${s}`]).length} chosen · ${seats[s]} seats`} />
        ))}
        {stale > 0 && <Chip size="small" color="warning" label={`${stale} dancer${stale === 1 ? ' had a workshop' : 's had workshops'} since removed from the schedule`} />}
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<PrintIcon />} onClick={() => adminClient.openPrintPdf(['workshop-rosters'])}>Rosters</Button>
        <Button size="small" startIcon={<PrintIcon />} onClick={() => adminClient.openPrintPdf(['workshops-by-school'])}>School lists</Button>
        <IconButton size="small" onClick={load} disabled={loading} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {data.runs.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No workshops are on the schedule yet — build it on the Schedule page first.</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="workshops" label="By workshop" />
        <Tab value="schools" label="By school" />
        {!readOnly && <Tab value="dancers" label="Move dancers" />}
      </Tabs>

      {tab === 'workshops' && <ByWorkshop data={data} />}
      {tab === 'schools' && <BySchool data={data} />}
      {tab === 'dancers' && !readOnly && <MoveDancers data={data} onSaved={({ changed, view }) => { setNotice({ severity: 'success', text: `Saved ${changed} change${changed === 1 ? '' : 's'}.` }); showView(view); }} />}

      <Dialog open={confirmRelease !== null} onClose={() => setConfirmRelease(null)}>
        <DialogTitle>{confirmRelease ? 'Release the schedule to teachers?' : 'Hide the schedule from teachers?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmRelease
              ? 'Teachers will see every workshop on their portal and can start choosing a Session 1 and a Session 2 workshop for each dancer. Check the schedule is final first — removing a workshop later unassigns the dancers who chose it.'
              : 'Teachers will see “coming soon” again and can’t make changes. Choices already made are kept.'}
          </DialogContentText>
          {confirmRelease && unconfirmed.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {unconfirmed.length} scheduled workshop{unconfirmed.length === 1 ? ' has a leader' : 's have leaders'} who {unconfirmed.length === 1 ? 'isn’t' : 'aren’t'} confirmed yet:
              {' '}{unconfirmed.map(r => `${r.title} (${r.leaderName})`).join(', ')}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRelease(null)}>Cancel</Button>
          <Button variant="contained" disabled={savingRelease} onClick={() => saveRelease({ released: confirmRelease })}>
            {confirmRelease ? 'Release' : 'Hide'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Each session's workshops with seats filled; a row opens its roster.
function ByWorkshop({ data }) {
  const [open, setOpen] = useState(null);
  return [1, 2].map(session => {
    const runs = data.runs.filter(r => r.session === session);
    return (
      <Box key={session} sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{sessionLabel(data.sessions[session])}</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Workshop</TableCell>
                <TableCell>Leader</TableCell>
                <TableCell>Space</TableCell>
                <TableCell sx={{ width: 200 }}>Dancers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No workshops in this session.</Typography></TableCell></TableRow>}
              {runs.map(r => {
                const isOpen = open === r.runId;
                const roster = data.dancers.filter(d => d[`session${session}`] === r.runId);
                const pct = r.capacity ? Math.min(100, (r.assigned / r.capacity) * 100) : 0;
                return (
                  <React.Fragment key={r.runId}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(isOpen ? null : r.runId)}>
                      <TableCell>{isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}</TableCell>
                      <TableCell>{r.title}</TableCell>
                      <TableCell>
                        {r.leaderName}
                        {!isConfirmedStatus(r.leaderStatus) && <Chip size="small" color="warning" variant="outlined" label="Not confirmed" sx={{ ml: 1, height: 20 }} />}
                      </TableCell>
                      <TableCell>{r.spaceName}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.assigned} / {r.capacity}</Typography>
                        <LinearProgress variant="determinate" value={pct} color={r.assigned > r.capacity ? 'error' : r.assigned === r.capacity ? 'success' : 'primary'} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ py: 1.5, columns: '2 260px' }}>
                            {roster.length === 0
                              ? <Typography variant="body2" color="text.secondary">No dancers yet.</Typography>
                              : roster.map(d => (
                                <Typography key={d.participantId} variant="body2" sx={{ breakInside: 'avoid' }}>
                                  {dancerName(d)} <Typography component="span" variant="body2" color="text.secondary">— {d.schoolName}</Typography>
                                </Typography>
                              ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  });
}

// Progress per school; a row opens its spots in every workshop.
function BySchool({ data }) {
  const [open, setOpen] = useState(null);
  if (data.schools.length === 0) return <Typography variant="body2" color="text.secondary">No attending dancers yet.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40 }} />
            <TableCell>School</TableCell>
            <TableCell align="right">Dancers</TableCell>
            <TableCell align="right">{data.sessions[1].name}</TableCell>
            <TableCell align="right">{data.sessions[2].name}</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.schools.map(s => {
            const isOpen = open === s.schoolId;
            return (
              <React.Fragment key={s.schoolId}>
                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(isOpen ? null : s.schoolId)}>
                  <TableCell>{isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}</TableCell>
                  <TableCell>{s.schoolName}</TableCell>
                  <TableCell align="right">{s.dancers}</TableCell>
                  <TableCell align="right">{s.session1}</TableCell>
                  <TableCell align="right">{s.session2}</TableCell>
                  <TableCell>
                    {s.complete === s.dancers
                      ? <Chip size="small" color="success" label="Done" />
                      : s.session1 + s.session2 === 0
                        ? <Chip size="small" label="Not started" />
                        : <Chip size="small" color="warning" label={`${s.dancers - s.complete} to go`} />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ py: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {[1, 2].map(session => (
                          <Box key={session}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{data.sessions[session].name} — spots used</Typography>
                            {data.runs.filter(r => r.session === session).map(r => {
                              const sp = s.spots[r.runId] || { spots: 0, taken: 0 };
                              return (
                                <Typography key={r.runId} variant="body2" color={sp.taken > sp.spots ? 'error' : 'text.primary'}>
                                  {r.title}: {sp.taken} / {sp.spots}
                                </Typography>
                              );
                            })}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Admin override: change any dancer's workshops, then save the changes together.
function MoveDancers({ data, onSaved }) {
  const [search, setSearch] = useState('');
  const [school, setSchool] = useState('');
  const [edits, setEdits] = useState({}); // participantId -> { session1, session2 }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const runsBySession = useMemo(() => ({
    1: data.runs.filter(r => r.session === 1),
    2: data.runs.filter(r => r.session === 2),
  }), [data.runs]);
  const wsByRun = useMemo(() => new Map(data.runs.map(r => [r.runId, r.workshopId])), [data.runs]);

  const shown = data.dancers.filter(d => {
    if (school && d.schoolId !== school) return false;
    const q = search.trim().toLowerCase();
    return !q || dancerName(d).toLowerCase().includes(q) || d.schoolName.toLowerCase().includes(q);
  });
  const value = (d, s) => (edits[d.participantId]?.[`session${s}`] ?? d[`session${s}`]);
  const setValue = (d, s, runId) => {
    const next = { session1: value(d, 1), session2: value(d, 2), [`session${s}`]: runId };
    setEdits(prev => {
      const copy = { ...prev };
      if (next.session1 === d.session1 && next.session2 === d.session2) delete copy[d.participantId];
      else copy[d.participantId] = next;
      return copy;
    });
  };
  const changeCount = Object.keys(edits).length;

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const result = await adminClient.saveWorkshopAssignments(Object.entries(edits).map(([participantId, e]) => ({ participantId, ...e })));
      setEdits({});
      onSaved(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search dancers or schools" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: 240 }} />
        <Select size="small" displayEmpty value={school} onChange={e => setSchool(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">All schools</MenuItem>
          {data.schools.map(s => <MenuItem key={s.schoolId} value={s.schoolId}>{s.schoolName}</MenuItem>)}
        </Select>
        <Box sx={{ flex: 1 }} />
        {changeCount > 0 && <Button size="small" onClick={() => setEdits({})} disabled={saving}>Discard</Button>}
        <Button size="small" variant="contained" disabled={!changeCount || saving} onClick={save}>
          {saving ? 'Saving…' : `Save ${changeCount || ''} change${changeCount === 1 ? '' : 's'}`}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Changes here skip each school’s spot limit, but a workshop can’t go over its seats.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dancer</TableCell>
              <TableCell>School</TableCell>
              <TableCell>{data.sessions[1].name}</TableCell>
              <TableCell>{data.sessions[2].name}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No dancers match.</Typography></TableCell></TableRow>}
            {shown.map(d => (
              <TableRow key={d.participantId} sx={edits[d.participantId] ? { bgcolor: 'action.selected' } : undefined}>
                <TableCell>{dancerName(d)}{d.stale && <Chip size="small" color="warning" label="Workshop removed" sx={{ ml: 1 }} />}</TableCell>
                <TableCell>{d.schoolName}</TableCell>
                {[1, 2].map(s => {
                  const other = wsByRun.get(value(d, s === 1 ? 2 : 1));
                  return (
                    <TableCell key={s}>
                      <Select size="small" displayEmpty fullWidth value={value(d, s)} onChange={e => setValue(d, s, e.target.value)} sx={{ minWidth: 180 }}>
                        <MenuItem value=""><em>Not chosen</em></MenuItem>
                        {runsBySession[s].map(r => (
                          <MenuItem key={r.runId} value={r.runId} disabled={r.workshopId === other}>
                            {r.title} — {r.spaceName} ({r.assigned}/{r.capacity})
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
